/**
 * How a fetched submission-link payload maps onto the public page's state.
 *
 * A leaf module on purpose. This decision used to live inline in a `useEffect`
 * in `src/app/submit/[slug].tsx`, where it could not be tested without
 * mounting the whole page — and where the bug below survived precisely
 * because nothing asserted it.
 *
 * WHY A PAUSED LINK IS NOT AN UNKNOWN LINK
 * ----------------------------------------
 * `GET /process-submission?slug=…` answers 200 with the investor's name, firm
 * and thesis message whether or not the link is accepting submissions —
 * `isActive` is one field of a complete payload, not an error shape.
 *
 * The page nevertheless did:
 *
 *     if (!data.isActive) { setLinkError('inactive'); return; }  // ← drops data
 *     setLinkData(data);
 *
 * so a paused link rendered its hero panel with `linkData === null`, and the
 * name fell through to the `'this investor'` placeholder — initials `TI`. A
 * founder following a real investor's link was told an anonymous stranger had
 * paused submissions, on the one screen whose entire job is to establish whose
 * link this is.
 *
 * Availability and identity are independent: resolve them independently.
 */

export type SubmissionLinkErrorType = 'not-found' | 'inactive' | 'server-error';

export interface SubmissionLinkPayload {
  investorName: string;
  investorFirm?: string | null;
  thesisMessage: string | null;
  isActive: boolean;
}

export interface SubmissionLinkState {
  /**
   * The investor's identity, whenever the server told us who they are —
   * INCLUDING when the link is paused. Null only when there is genuinely
   * nobody to name.
   */
  linkData: SubmissionLinkPayload | null;
  /** Why the form cannot be shown, or null when it can. */
  error: SubmissionLinkErrorType | null;
}

/**
 * Maps a successful (HTTP 200) link lookup onto page state.
 *
 * Identity is kept in every case the server supplied it; `error` reflects only
 * whether submissions are open.
 */
export function resolveSubmissionLinkState(
  payload: SubmissionLinkPayload
): SubmissionLinkState {
  return {
    linkData: payload,
    error: payload.isActive ? null : 'inactive',
  };
}

/** Maps a non-200 lookup onto page state. There is no identity to keep here. */
export function resolveSubmissionLinkHttpError(status: number): SubmissionLinkState {
  return {
    linkData: null,
    error: status === 404 ? 'not-found' : 'server-error',
  };
}
